import { useUploadEmployeeAttachmentsMutation } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import { DOCUMENT_TYPES } from '@/app/features/employee/emp-dashboard/emp-dashboard.type';
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import { UploadIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Flex, Form, Input, Select, Upload } from 'antd';
import type { UploadFile } from 'antd';
import { useEffect, useState } from 'react';

const { Dragger } = Upload;

const ACCEPT = '.pdf,.png,.jpeg,.jpg,.doc,.docx';

type Props = CustomModalProps & {
  employeeId: string;
};

/**
 * Upload attachments for one employee.
 *
 * The files go to this endpoint directly as multipart, so nothing is uploaded
 * until the drawer is submitted and a failed save leaves no orphan file behind.
 * One submit can carry several files under the same name and type.
 */
const ManageAttachment = ({ open, handleClose, employeeId }: Props) => {
  const [form] = Form.useForm();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploadAttachments, { isLoading }] =
    useUploadEmployeeAttachmentsMutation();

  useEffect(() => {
    if (open) {
      form.resetFields();
      setFiles([]);
    }
  }, [open, form]);

  const handleSubmit = async (values: any) => {
    const selected = files
      .map((file) => file.originFileObj as File)
      .filter(Boolean);

    if (!selected.length) {
      toast('error', 'Please select at least one file');
      return;
    }

    try {
      const result = await uploadAttachments({
        employeeId,
        documentName: values.documentName?.trim(),
        documentType: values.documentType,
        files: selected,
      }).unwrap();

      const skipped = result?.data?.skipped?.length || 0;
      toast('success', 'Document uploaded successfully');
      if (skipped) {
        toast('warning', `${skipped} file(s) were skipped by the server`);
      }
      handleClose();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to upload the document'));
    }
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose}
      title="Upload Document"
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        onFinish={handleSubmit}
        className="custom-form space-y-6"
      >
        <Form.Item
          label="Document Name"
          name="documentName"
          rules={[{ required: true, message: 'Please enter a name' }]}
        >
          <Input placeholder="NID front side" />
        </Form.Item>

        <Form.Item
          label="Document Type"
          name="documentType"
          rules={[{ required: true, message: 'Please select a type' }]}
        >
          <Select placeholder="Select" options={[...DOCUMENT_TYPES]} />
        </Form.Item>

        <div className="space-y-2">
          <span className="text-sm text-white/45">File</span>
          <Dragger
            multiple
            accept={ACCEPT}
            fileList={files}
            // `false` keeps the file local; it is sent on submit.
            beforeUpload={() => false}
            onChange={({ fileList }) => setFiles(fileList)}
            className="!border-none !bg-transparent"
            style={{
              borderWidth: 2,
              borderStyle: 'dashed',
              borderRadius: 12,
            }}
          >
            <div className="flex flex-col items-center gap-3 py-6">
              <span className="size-12 rounded-xl bg-theme-slate-700 flex items-center justify-center">
                <UploadIcon size={24} className="text-gray-400" />
              </span>
              <p className="text-white/70 text-base">
                Drag &amp; drop your files here
              </p>
              <span className="text-theme-primary text-sm font-semibold underline underline-offset-2">
                Browse Files
              </span>
              <p className="text-xs text-gray-500">pdf, image or doc</p>
            </div>
          </Dragger>
        </div>

        <Flex justify="end">
          <Button
            htmlType="submit"
            loading={isLoading}
            className="btn-primary min-w-[80px] h-11 font-semibold"
          >
            Upload
          </Button>
        </Flex>
      </Form>
    </CustomDrawer>
  );
};

export default ManageAttachment;
